import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { siscontService } from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';

const EMPTY = {
  anio: new Date().getFullYear(),
  host: '',
  puerto: 1433,
  nombre_bd: '',
  usuario: '',
  password: '',
  driver: '{ODBC Driver 17 for SQL Server}',
  observacion: '',
};

const fmtFchFull = (d) => {
  if (!d) return '—';
  const s = String(d);
  const [date, timeRaw] = s.split('T');
  const time = (timeRaw || '').slice(0, 8);
  return time ? `${date} ${time}` : date;
};

const StatusBadge = ({ ok, fecha }) => {
  const cfg = ok === true
    ? { color: '#22c55e', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.35)', label: '● Conectado' }
    : ok === false
      ? { color: '#f87171', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.35)', label: '● Falló' }
      : { color: '#94a3b8', bg: 'rgba(148,163,184,0.10)', border: 'rgba(148,163,184,0.25)', label: '○ Sin probar' };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 6,
      fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
      color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
    }} title={fecha ? `Último test: ${fmtFchFull(fecha)}` : ''}>
      {cfg.label}
    </span>
  );
};

export default function SiscontConexiones() {
  const { user } = useAuth();
  const esAdmin = user?.id_perfil === 1;

  const [records,   setRecords]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [alert,     setAlert]     = useState(null);
  const [toast,     setToast]     = useState(null);
  const [confirm,   setConfirm]   = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(EMPTY);
  const [saving,    setSaving]    = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [testingAll,setTestingAll]= useState(false);
  const [testingForm, setTestingForm] = useState(false);
  const [testResult,  setTestResult]  = useState(null); // { ok, error }
  const [showPass,    setShowPass]    = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await siscontService.getAll();
      setRecords(r.data || []);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cargar conexiones.') });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (esAdmin) fetchData(); }, [fetchData, esAdmin]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setTestResult(null);
    setShowModal(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      anio:        r.anio || '',
      host:        r.host || '',
      puerto:      r.puerto || 1433,
      nombre_bd:   r.nombre_bd || '',
      usuario:     r.usuario || '',
      password:    '',
      driver:      r.driver || '{ODBC Driver 17 for SQL Server}',
      observacion: r.observacion || '',
    });
    setTestResult(null);
    setShowModal(true);
  };
  const setF = (k, v) => {
    setForm(prev => ({ ...prev, [k]: v }));
    setTestResult(null);
  };

  const probarDesdeFormulario = async () => {
    if (!form.host || !form.nombre_bd || !form.usuario || !form.password) {
      setTestResult({ ok: false, error: 'Complete servidor, nombre de BD, usuario y contraseña para probar.' });
      return;
    }
    setTestingForm(true);
    setTestResult(null);
    try {
      const res = await siscontService.probarDatos({
        anio:       parseInt(form.anio, 10) || 0,
        host:       form.host,
        puerto:     form.puerto ? parseInt(form.puerto, 10) : null,
        nombre_bd:  form.nombre_bd,
        usuario:    form.usuario,
        password:   form.password,
        driver:     form.driver || '{ODBC Driver 17 for SQL Server}',
      });
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ ok: false, error: getErrorDetail(err, 'Error al probar conexión.') });
    } finally { setTestingForm(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.anio)       { setAlert({ type: 'warning', message: 'El año es obligatorio.' }); return; }
    if (!form.host)       { setAlert({ type: 'warning', message: 'El host/servidor es obligatorio.' }); return; }
    if (!form.nombre_bd)  { setAlert({ type: 'warning', message: 'El nombre de la BD es obligatorio.' }); return; }
    if (!form.usuario)    { setAlert({ type: 'warning', message: 'El usuario es obligatorio.' }); return; }
    if (!editing && !form.password) {
      setAlert({ type: 'warning', message: 'La contraseña es obligatoria al crear una conexión.' }); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, anio: parseInt(form.anio, 10), puerto: form.puerto ? parseInt(form.puerto, 10) : null };
      if (editing) {
        if (!payload.password) delete payload.password; // no tocar la guardada
        await siscontService.update(editing.id_conexion, payload);
        setToast({ type: 'success', message: 'Conexión actualizada.' });
      } else {
        await siscontService.create(payload);
        setToast({ type: 'success', message: 'Conexión creada.' });
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar.') });
    } finally { setSaving(false); }
  };

  const handleDelete = (r) => {
    setConfirm({
      title: 'Eliminar conexión SISCONT',
      message: `¿Eliminar la conexión del año ${r.anio} (${r.nombre_bd})?`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await siscontService.delete(r.id_conexion);
          setToast({ type: 'success', message: 'Conexión eliminada.' });
          fetchData();
        } catch (err) {
          setAlert({ type: 'error', message: getErrorDetail(err, 'Error al eliminar.') });
        }
      },
    });
  };

  const probar = async (r) => {
    setTestingId(r.id_conexion);
    try {
      const res = await siscontService.probar(r.id_conexion);
      if (res.data?.ok) {
        setToast({ type: 'success', message: `Conexión al año ${r.anio} exitosa.` });
      } else {
        setToast({ type: 'error', message: `Falló conexión al año ${r.anio}: ${res.data?.error || 'error desconocido'}` });
      }
      fetchData();
    } catch (err) {
      setToast({ type: 'error', message: getErrorDetail(err, 'Error al probar conexión.') });
    } finally { setTestingId(null); }
  };

  const probarTodas = async () => {
    if (records.length === 0) return;
    setTestingAll(true);
    try {
      for (const r of records) {
        try { await siscontService.probar(r.id_conexion); } catch {}
      }
      fetchData();
      setToast({ type: 'success', message: 'Pruebas completadas.' });
    } finally { setTestingAll(false); }
  };

  if (!esAdmin) {
    return (
      <Layout title="Años Contables (SISCONT)" subtitle="Contabilidad">
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', background: 'rgba(15,27,45,0.4)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#f87171', marginBottom: 6 }}>⚠ Acceso restringido</div>
          Solo los administradores pueden ver y gestionar las conexiones SISCONT.
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Años Contables (SISCONT)" subtitle="Contabilidad">
      {alert   && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}
      {toast   && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
      {confirm && <Confirm {...confirm} onCancel={() => setConfirm(null)} />}

      {/* Encabezado */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0 }}>Conexiones SISCONT por Año</h2>
          <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 4 }}>
            Cada año contable tiene su propia base de datos. Desde aquí se monitorea la conexión y se probará antes de transferir asientos.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={probarTodas}
            disabled={testingAll || records.length === 0}>
            {testingAll ? 'Probando...' : '⟳ Probar todas'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={fetchData}>↺ Actualizar</button>
          <button className="btn btn-primary btn-sm" onClick={openNew}>+ Nueva conexión</button>
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
      ) : records.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b', background: 'rgba(15,27,45,0.3)', border: '1px dashed rgba(148,163,184,0.2)', borderRadius: 12 }}>
          Todavía no hay conexiones configuradas. Crea la primera con <strong>"+ Nueva conexión"</strong>.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))' }}>
          {records.map(r => (
            <div key={r.id_conexion} style={{
              background: 'rgba(15,27,45,0.5)', border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10,
              borderLeft: `4px solid ${
                r.ultimo_test_ok === true  ? '#22c55e' :
                r.ultimo_test_ok === false ? '#ef4444' : '#475569'
              }`,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#e2e8f0', fontFamily: 'monospace', lineHeight: 1 }}>
                    {r.anio}
                  </div>
                  <div style={{ fontSize: 10.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>
                    Año contable
                  </div>
                </div>
                <StatusBadge ok={r.ultimo_test_ok} fecha={r.ultimo_test_fecha} />
              </div>

              <div style={{ fontSize: 12, color: '#cbd5e1', display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div><span style={{ color: '#64748b' }}>Servidor:</span> <span style={{ fontFamily: 'monospace' }}>{r.host}{r.puerto ? `,${r.puerto}` : ''}</span></div>
                <div><span style={{ color: '#64748b' }}>Base de datos:</span> <span style={{ fontFamily: 'monospace', color: '#93c5fd', fontWeight: 600 }}>{r.nombre_bd}</span></div>
                <div><span style={{ color: '#64748b' }}>Usuario:</span> {r.usuario}</div>
                {r.observacion && (
                  <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', marginTop: 4 }}>{r.observacion}</div>
                )}
              </div>

              {r.ultimo_test_error && r.ultimo_test_ok === false && (
                <div style={{
                  fontSize: 10.5, color: '#fca5a5', padding: '6px 9px', borderRadius: 6,
                  background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                  fontFamily: 'monospace', lineHeight: 1.45, wordBreak: 'break-word',
                }}>
                  {r.ultimo_test_error}
                </div>
              )}

              <div style={{ fontSize: 10.5, color: '#64748b' }}>
                {r.ultimo_test_fecha ? `Último test: ${fmtFchFull(r.ultimo_test_fecha)}` : 'Sin prueba todavía'}
              </div>

              <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
                <button className="btn btn-primary btn-sm" style={{ flex: 1 }}
                  onClick={() => probar(r)} disabled={testingId === r.id_conexion}>
                  {testingId === r.id_conexion ? 'Probando...' : '⚡ Probar'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => openEdit(r)}>Editar</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(r)}>Eliminar</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal crear/editar */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? `Editar conexión SISCONT ${editing.anio}` : 'Nueva conexión SISCONT'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body" style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>

                {/* ── Sección: Identificación ── */}
                <Section title="Identificación" color="#60a5fa" icon={<IconCal/>}>
                  <Field label="Año contable" required hint="Cada año de SISCONT es una BD independiente">
                    <InputField
                      type="number" min="2000" max="2100"
                      value={form.anio} onChange={v => setF('anio', v)}
                      placeholder="2026" required autoFocus style={{ maxWidth: 160 }}
                    />
                  </Field>
                </Section>

                {/* ── Sección: Servidor ── */}
                <Section title="Servidor y base de datos" color="#60a5fa" icon={<IconServer/>}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 10 }}>
                    <Field label="Servidor / Host" required hint="IP, nombre o HOST\INSTANCIA">
                      <InputField
                        value={form.host} onChange={v => setF('host', v)}
                        placeholder="192.168.1.50 o SRV01\SQLEXPRESS" required
                        leading={<IconHost/>}
                      />
                    </Field>
                    <Field label="Puerto" hint="Default 1433">
                      <InputField
                        type="number" min="0" max="65535"
                        value={form.puerto || ''} onChange={v => setF('puerto', v)}
                        placeholder="1433"
                      />
                    </Field>
                  </div>
                  <Field label="Base de datos" required hint="Nombre exacto de la base de datos SISCONT del año">
                    <InputField
                      value={form.nombre_bd} onChange={v => setF('nombre_bd', v)}
                      placeholder="Ej: SISCONT_EMPRESA_2026" required
                      leading={<IconDb/>}
                      mono
                    />
                  </Field>
                </Section>

                {/* ── Sección: Credenciales ── */}
                <Section title="Credenciales SQL" color="#a78bfa" icon={<IconLock/>}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="Usuario SQL" required>
                      <InputField
                        value={form.usuario} onChange={v => setF('usuario', v)}
                        placeholder="sa" required
                        leading={<IconUser/>}
                        autoComplete="off"
                      />
                    </Field>
                    <Field
                      label={`Contraseña${editing ? ' (vacío = mantener)' : ''}`}
                      required={!editing}
                      rightSlot={
                        <button type="button" onClick={() => setShowPass(p => !p)}
                          tabIndex={-1}
                          style={{
                            fontSize: 10.5, padding: '1px 7px', borderRadius: 4, cursor: 'pointer',
                            background: 'rgba(255,255,255,0.04)', color: '#64748b',
                            border: '1px solid rgba(255,255,255,0.08)',
                          }}>
                          {showPass ? '○ ocultar' : '● ver'}
                        </button>
                      }
                    >
                      <InputField
                        type={showPass ? 'text' : 'password'}
                        value={form.password} onChange={v => setF('password', v)}
                        placeholder={editing ? '••••••••' : ''}
                        autoComplete="new-password"
                        leading={<IconKey/>}
                        mono={showPass}
                      />
                    </Field>
                  </div>
                </Section>

                {/* ── Bloque Probar Conexión ── */}
                <div style={{
                  padding: 12, borderRadius: 10,
                  background: testResult?.ok === true  ? 'rgba(34,197,94,0.08)'
                          : testResult?.ok === false  ? 'rgba(239,68,68,0.08)'
                          : 'rgba(96,165,250,0.07)',
                  border: `1px solid ${
                    testResult?.ok === true  ? 'rgba(34,197,94,0.3)'
                  : testResult?.ok === false  ? 'rgba(239,68,68,0.3)'
                  : 'rgba(96,165,250,0.2)'
                  }`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <button type="button"
                      onClick={probarDesdeFormulario} disabled={testingForm}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 7,
                        padding: '8px 16px', fontSize: 12.5, fontWeight: 700, borderRadius: 8,
                        background: testingForm ? 'rgba(96,165,250,0.08)' : 'rgba(96,165,250,0.15)',
                        color: '#93c5fd', border: '1px solid rgba(96,165,250,0.4)',
                        cursor: testingForm ? 'wait' : 'pointer',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      {testingForm
                        ? <><Spin/> Probando...</>
                        : <><IconBolt/> Probar conexión</>}
                    </button>
                    <div style={{ fontSize: 12, flex: 1, minWidth: 0 }}>
                      {testResult?.ok === true && (
                        <div style={{ color: '#22c55e', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <IconCheck/> Conexión exitosa — parámetros válidos
                        </div>
                      )}
                      {testResult?.ok === false && (
                        <div style={{ color: '#fca5a5' }}>
                          <div style={{ fontWeight: 700, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <IconX/> La conexión falló
                          </div>
                          <div style={{
                            fontFamily: 'monospace', fontSize: 10.5, color: '#fca5a5',
                            lineHeight: 1.5, wordBreak: 'break-word', maxHeight: 100, overflowY: 'auto',
                          }}>
                            {testResult.error}
                          </div>
                        </div>
                      )}
                      {!testResult && !testingForm && (
                        <div style={{ color: '#64748b', fontSize: 11.5 }}>
                          Recomendado antes de guardar — valida servidor, base de datos y credenciales.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}


/* ── Subcomponentes de UI ──────────────────────────────────────── */

const Section = ({ title, color = '#60a5fa', icon, children }) => (
  <div style={{
    padding: '12px 14px 14px',
    background: 'rgba(15,27,45,0.45)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderLeft: `3px solid ${color}`,
    borderRadius: 10,
    display: 'flex', flexDirection: 'column', gap: 10,
  }}>
    <div style={{
      fontSize: 10.5, fontWeight: 800, color, textTransform: 'uppercase',
      letterSpacing: 0.8, display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <span style={{ opacity: 0.9, display: 'inline-flex' }}>{icon}</span>
      {title}
    </div>
    {children}
  </div>
);

const Field = ({ label, required, hint, rightSlot, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <label style={{
        fontSize: 10.5, fontWeight: 700, color: '#94a3b8',
        textTransform: 'uppercase', letterSpacing: 0.6,
      }}>
        {label} {required && <span style={{ color: '#f87171' }}>*</span>}
      </label>
      {rightSlot}
    </div>
    {children}
    {hint && <div style={{ fontSize: 10.5, color: '#64748b' }}>{hint}</div>}
  </div>
);

const InputField = React.forwardRef(function InputField(
  { value, onChange, leading, mono, style = {}, ...rest }, ref
) {
  const [focus, setFocus] = React.useState(false);
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      height: 36, padding: 0, borderRadius: 8,
      background: 'rgba(255,255,255,0.04)',
      border: `1px solid ${focus ? '#3b82f6' : 'rgba(255,255,255,0.09)'}`,
      boxShadow: focus ? '0 0 0 3px rgba(59,130,246,0.12)' : 'none',
      transition: 'border-color 0.15s, box-shadow 0.15s',
      overflow: 'hidden',
      ...style,
    }}>
      {leading && (
        <span style={{
          width: 34, height: '100%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: focus ? '#93c5fd' : '#64748b',
          background: 'rgba(15,27,45,0.4)',
          borderRight: '1px solid rgba(255,255,255,0.06)',
        }}>
          {leading}
        </span>
      )}
      <input
        ref={ref}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={{
          flex: 1, height: '100%', width: '100%',
          padding: '0 12px',
          background: 'transparent',
          border: 'none', outline: 'none',
          color: '#e2e8f0',
          fontSize: 13,
          fontFamily: mono ? 'Consolas, monospace' : 'Inter, sans-serif',
          boxSizing: 'border-box',
        }}
        {...rest}
      />
    </div>
  );
});

/* ── Íconos (stroke inline) ────────────────────────────────────── */

const IconBase = ({ children, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);
const IconCal    = () => <IconBase><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></IconBase>;
const IconServer = () => <IconBase><rect x="2" y="3" width="20" height="7" rx="2"/><rect x="2" y="14" width="20" height="7" rx="2"/><path d="M6 6.5h.01M6 17.5h.01"/></IconBase>;
const IconHost   = () => <IconBase><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 12h20M12 4v16"/></IconBase>;
const IconDb     = () => <IconBase><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v6c0 1.66 4 3 9 3s9-1.34 9-3V5M3 11v6c0 1.66 4 3 9 3s9-1.34 9-3v-6"/></IconBase>;
const IconLock   = () => <IconBase><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></IconBase>;
const IconUser   = () => <IconBase><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></IconBase>;
const IconKey    = () => <IconBase><path d="M21 2l-9.6 9.6"/><circle cx="7.5" cy="15.5" r="5.5"/><path d="M15.5 7.5l3 3M13.5 9.5l3 3"/></IconBase>;
const IconBolt   = () => <IconBase><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></IconBase>;
const IconCheck  = () => <IconBase><path d="M20 6L9 17l-5-5"/></IconBase>;
const IconX      = () => <IconBase><path d="M18 6L6 18M6 6l12 12"/></IconBase>;
const Spin       = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    style={{ animation: 'spin 0.8s linear infinite' }}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25"/>
    <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
    <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
  </svg>
);
